// ============================================
// COMPONENT - EditarSucursalModal
// ============================================

import { useState, useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import { useActualizarSucursal, useUIStore } from '@almacenes/core';
import type { Sucursal, ActualizarSucursalDto } from '@almacenes/core';

interface EditarSucursalModalProps {
  isOpen: boolean;
  onClose: () => void;
  sucursal: Sucursal | null;
}

export const EditarSucursalModal = ({ isOpen, onClose, sucursal }: EditarSucursalModalProps) => {
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    numeroDireccion: '',
  });

  const actualizarSucursalMutation = useActualizarSucursal();
  const addToast = useUIStore((state) => state.addToast);

  // Cargar datos de la sucursal cuando se abre el modal
  useEffect(() => {
    if (sucursal && isOpen) {
      setFormData({
        nombre: sucursal.nombre,
        direccion: sucursal.direccion,
        numeroDireccion: sucursal.numeroDireccion.toString(),
      });
    }
  }, [sucursal, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sucursal) return;

    // Validación
    if (!formData.nombre.trim()) {
      addToast('error', 'El nombre es obligatorio');
      return;
    }
    if (!formData.direccion.trim()) {
      addToast('error', 'La dirección es obligatoria');
      return;
    }
    if (!formData.numeroDireccion || Number(formData.numeroDireccion) <= 0) {
      addToast('error', 'El número de dirección es obligatorio y debe ser mayor a 0');
      return;
    }

    const datos: ActualizarSucursalDto = {
      nombre: formData.nombre.trim(),
      direccion: formData.direccion.trim(),
      numeroDireccion: Number(formData.numeroDireccion),
    };

    try {
      await actualizarSucursalMutation.mutateAsync({ sucursalId: sucursal.id, datos });
      addToast('success', 'Sucursal actualizada correctamente');
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al actualizar la sucursal';
      addToast('error', errorMessage);
    }
  };

  const handleClose = () => {
    setFormData({ nombre: '', direccion: '', numeroDireccion: '' });
    onClose();
  };

  if (!isOpen || !sucursal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
            Editar Sucursal
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={actualizarSucursalMutation.isPending}
          >
            <MdClose className="text-2xl" style={{ color: '#11194F' }} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              placeholder="Ingrese el nombre de la sucursal"
              disabled={actualizarSucursalMutation.isPending}
            />
          </div>

          {/* Dirección */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Dirección <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.direccion}
              onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              placeholder="Ingrese la dirección"
              disabled={actualizarSucursalMutation.isPending}
            />
          </div>

          {/* Número de Dirección */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Número <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.numeroDireccion}
              onChange={(e) => setFormData({ ...formData, numeroDireccion: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              placeholder="Ingrese el número de dirección"
              min="1"
              disabled={actualizarSucursalMutation.isPending}
            />
          </div>

          {/* Info readonly */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Comuna:</span> {sucursal.comuna}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              style={{ color: '#11194F' }}
              disabled={actualizarSucursalMutation.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[#43BFFF] text-white rounded-lg font-medium hover:bg-[#3AABEB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={actualizarSucursalMutation.isPending}
            >
              {actualizarSucursalMutation.isPending && (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              )}
              {actualizarSucursalMutation.isPending ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
